import React from 'react';
import { TextField as MuiTextField, styled, IconButton } from '@mui/material';
import { SvgIcon } from '@mui/material';

/**
 * Custom TextField component with enhanced styling and number input support.
 * 
 * Features:
 * - Consistent modern design across light/dark themes
 * - Custom number input with increment/decrement arrows
 * - Proper label positioning and styling
 * - Disabled and error state handling
 * - Multiline support with proper spacing
 * 
 * @example
 * // Basic text input
 * <TextField label="Name" value={name} onChange={handleChange} />
 * 
 * // Number input with custom range
 * <TextField 
 *   type="number" 
 *   label="Age" 
 *   value={age} 
 *   onChange={handleAgeChange}
 *   InputProps={{
 *     inputProps: { min: 0, max: 120 }
 *   }}
 * />
 * 
 * // Multiline text area
 * <TextField 
 *   multiline 
 *   rows={4} 
 *   label="Description" 
 *   value={description} 
 *   onChange={handleDescriptionChange}
 * />
 */

const StyledTextField = styled(MuiTextField)(({ theme, multiline }) => {
  // Helper function to get background color based on theme and state
  const getBackgroundColor = (isHovered = false) => {
    const baseColor = theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)';
    const hoverColor = theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)';
    return isHovered ? hoverColor : baseColor;
  };

  return {
    '& .MuiFormLabel-root': {
      color: theme.palette.text.secondary,
      fontSize: '0.875rem',
      transform: 'none',
      position: 'relative',
      marginBottom: theme.spacing(1),
      '&.Mui-focused': {
        color: theme.palette.text.secondary,
      },
      '&.Mui-error': {
        color: theme.palette.error.main,
      },
    },

    // Hide default number input arrows
    '& input[type="number"]::-webkit-outer-spin-button, & input[type="number"]::-webkit-inner-spin-button': {
      WebkitAppearance: 'none',
      margin: 0,
    },

    '& input[type="number"]': {
      MozAppearance: 'textfield',
    },

    '& .MuiInputBase-root': {
      height: multiline ? 'auto' : '48px',
      borderRadius: '12px',
      backgroundColor: getBackgroundColor(),
      transition: theme.transitions.create([
        'border-color',
        'background-color',
        'box-shadow',
      ]),

      '&:hover:not(.Mui-disabled)': {
        backgroundColor: getBackgroundColor(true),
      },

      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: 'transparent',
        legend: {
          display: 'none',
        },
      },

      '&.Mui-focused': {
        '& .MuiOutlinedInput-notchedOutline': {
          borderColor: theme.palette.primary.main,
          borderWidth: '1px',
        },
      },

      // Style for the end adornment (icons, buttons)
      '& .MuiInputAdornment-root': {
        marginRight: theme.spacing(1),
        height: '100%',
        maxHeight: 'none',
      },

      // Consolidated input styles with high specificity
      '& .MuiOutlinedInput-root .MuiOutlinedInput-input': {
        padding: multiline ? '0' : '0 16px',
        height: multiline ? 'auto' : '48px',
        margin: 0,
        textOverflow: 'ellipsis',
        whiteSpace: multiline ? 'pre-wrap' : 'nowrap',
        overflow: 'hidden',
        '&::placeholder': {
          opacity: 0.7,
          textOverflow: 'ellipsis',
          whiteSpace: multiline ? 'pre-wrap' : 'nowrap',
          overflow: 'hidden',
        },
      },
    },

    // Remove any fieldset padding
    '& .MuiOutlinedInput-notchedOutline': {
      top: 0,
    },

    // Disabled state
    '& .Mui-disabled': {
      opacity: 0.7,
      '& .MuiOutlinedInput-notchedOutline': {
        border: 'none',
      },
    },

    // Error state
    '& .Mui-error': {
      marginLeft: 0,
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.palette.error.main,
      },
    },

    // Autofill styles
    '& input:-webkit-autofill, & input:-webkit-autofill:hover, & input:-webkit-autofill:focus': {
      transition: 'background-color 5000s ease-in-out 0s',
      WebkitTextFillColor: theme.palette.text.primary,
      WebkitBoxShadow: 'none',
      height: '1em',
    },

    // Helper text margins
    '& .MuiFormHelperText-root': {
      marginLeft: 0,
      marginRight: 0,
    },
  };
});

// Custom arrow icons for number input
const TriangleUp = (props) => (
  <SvgIcon {...props} viewBox="0 0 8 8">
    <path d="M4 2L7 6H1L4 2Z" />
  </SvgIcon>
);

const TriangleDown = (props) => (
  <SvgIcon {...props} viewBox="0 0 8 8">
    <path d="M4 6L1 2H7L4 6Z" />
  </SvgIcon>
);

// Styled components for number input arrows
const NumberArrowButton = styled(IconButton)(({ theme }) => ({
  padding: 0,
  minWidth: '14px',
  width: '14px',
  height: '12px',
  color: theme.palette.text.primary,
  opacity: 0.7,
  '&:hover': {
    opacity: 1,
    backgroundColor: 'transparent',
  },
  '& .MuiSvgIcon-root': {
    fontSize: '12px',
  },
}));

const NumberArrowsContainer = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  marginRight: 0,
  gap: 0,
});

/**
 * Enhanced TextField component with custom styling and number input support.
 * 
 * @param {Object} props - Component props
 * @param {string} props.label - Field label
 * @param {Object} props.InputProps - Props for the input element
 * @param {boolean} props.multiline - Whether to render as multiline textarea
 * @param {number} props.rows - Number of rows for multiline input (default: 1)
 * @param {string} props.type - Input type (text, number, etc.)
 * @param {Function} props.onChange - Change handler function
 * @param {any} props.value - Input value
 * @param {string} props.name - Input name attribute
 * @param {Object} props - Additional props passed to MUI TextField
 * @param {React.Ref} ref - Forwarded ref
 */
const TextField = React.forwardRef(
  (
    { label, InputProps, multiline, rows = 1, type, onChange, value, ...props },
    ref
  ) => {
    // Get min value for number inputs
    const min = type === 'number' 
      ? InputProps?.inputProps?.min !== undefined ? Number(InputProps.inputProps.min) : 1
      : undefined;

    // Handle number input changes with validation
    const handleNumberChange = (e) => {
      let val = parseInt(e.target.value, 10);
      if (isNaN(val) || (min !== undefined && val < min)) val = min;
      if (onChange) onChange({ target: { name: props.name, value: val } });
    };

    // Handle increment/decrement for number inputs
    const handleStep = (step) => (e) => {
      e.preventDefault();
      let currentValue = parseFloat(value);
      if (isNaN(currentValue)) currentValue = min;
      let newValue = currentValue + step;
      if (min !== undefined && newValue < min) newValue = min;
      const max = InputProps?.inputProps?.max !== undefined ? Number(InputProps.inputProps.max) : undefined;
      if (max !== undefined && newValue > max) newValue = max;
      if (onChange) onChange({ target: { name: props.name, value: newValue } });
    };

    // Get input props with custom number arrows if needed
    const getNumberInputProps = () => {
      if (type !== 'number') return InputProps;

      return {
        ...InputProps,
        endAdornment: (
          <NumberArrowsContainer>
            <NumberArrowButton size="small" onClick={handleStep(1)}>
              <TriangleUp />
            </NumberArrowButton>
            <NumberArrowButton size="small" onClick={handleStep(-1)}>
              <TriangleDown />
            </NumberArrowButton>
          </NumberArrowsContainer>
        ),
      };
    };

    return (
      <StyledTextField
        variant="outlined"
        label={label}
        multiline={multiline}
        rows={rows}
        type={type}
        value={value}
        onChange={type === 'number' ? handleNumberChange : onChange}
        InputLabelProps={{
          shrink: true,
        }}
        InputProps={getNumberInputProps()}
        {...props}
        ref={ref}
      />
    );
  }
);

TextField.displayName = 'TextField';

export default TextField;
